import type { ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates raw portion options for an ingredient in a paired included/extras group.
 *
 * Returns undefined when the combined max exceeds the stepper limit.
 */
export function getPortionOptions(params: Params): PortionOption[] | undefined {
  const {
    ingredientId,
    currentPortions,
    groupActiveCount,
    maxPortions,
    freePortions,
    allModificationsMap,
    extrasModificationsMap,
  } = params;

  if (!ingredientId || !allModificationsMap || maxPortions <= 1) return;
  if (maxPortions > MAX_PORTION_STEPPER_LIMIT) return;

  const modification = allModificationsMap.get(ingredientId);
  const extrasModification = modification?.ingredientId
    ? extrasModificationsMap.get(modification.ingredientId)
    : undefined;

  const extrasPrice = extrasModification?.additionCost ?? 0;
  const otherIngredientsCount = groupActiveCount - currentPortions;
  const availablePortions = maxPortions - otherIngredientsCount;
  const visiblePortions = Math.min(availablePortions, MAX_VISIBLE_PORTION_OPTIONS);

  return Array.from({ length: visiblePortions }, (_, index) => {
    const portions = index + 1;
    const extraPortions = Math.max(0, portions - freePortions);

    return { portions, extrasCost: extraPortions * extrasPrice };
  });
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MAX_PORTION_STEPPER_LIMIT = 4;
const MAX_VISIBLE_PORTION_OPTIONS = 3;

// ─── Types ──────────────────────────────────────────────────────────────────

export type PortionOption = {
  portions: number;
  extrasCost: number;
};

type Params = {
  ingredientId: string | undefined;
  currentPortions: number;
  groupActiveCount: number;
  maxPortions: number;
  freePortions: number;
  allModificationsMap: ModificationsMap | undefined;
  extrasModificationsMap: ModificationsMap;
};
